import { TEAM_STORE_METAOBJECT_QUERY } from '@theme/api-queries';
import { createStorefrontClient } from '@theme/storefront-api';

/**
 * @typedef {object} TeamProductData
 * @property {string|null} [teamParentSku]
 * @property {string|null} [opportunityNumber]
 * @property {string} [domain]
 * @property {string} [priceLevel]
 * @property {boolean} [isTeamAdmin]
 * @property {Record<string, any>} [metaobject]
 * @property {Record<string, any>} [teamStoreCollection]
 */

/**
 * @typedef {object} MetaobjectField
 * @property {string} key
 * @property {string} value
 * @property {string} type
 */

/**
 * @typedef {object} TeamStoreMetafield
 * @property {string} key
 * @property {string} value
 */

/**
 * @typedef {object} TeamStoreCollectionReference
 * @property {string} id
 * @property {string|null} onlineStoreUrl
 * @property {(TeamStoreMetafield|null)[]|null} metafields
 */

/**
 * @typedef {object} TeamStoreCollectionField
 * @property {{ reference: TeamStoreCollectionReference|null }|null} team_store_collection
 */

/**
 * @typedef {object} TeamPdpMetaobjectNode
 * @property {MetaobjectField[]|null} fields
 * @property {{ reference: TeamStoreCollectionReference|null }|null} team_store_collection
 */

/**
 * @typedef {object} TeamPdpMetaobjectQueryData
 * @property {TeamPdpMetaobjectNode|null} metaobject
 */

/**
 * TeamPdpMetaobjectService
 * ------------------------
 * Encapsulates fetching and normalizing the Team PDP metaobject
 * its Team Store collection metafields, and wiring it into
 * teamProductData + DOM events.
 */
export class TeamPdpMetaobjectService {
  /**
   * @param {object} [opts]
   * @param {Window} [opts.win=window] - Window-like object (for testing).
   */
  constructor({ win = window } = {}) {
    this.client = createStorefrontClient({
      token: window.shopifyVariables.STOREFRONT_ACCESS_TOKEN,
    });
    this.win = win;
  }

  /**
   * @param {string} teamParentSku
   * Fetch Team PDP metaobject for given teamProductData and update it in-place.
   * @returns {Promise<{ metaobject: Record<string, any>|null, teamStoreCollection: Record<string, any>|null }>}
   */
  async load(teamParentSku) {
    const metaobjectHandle = this._buildHandle(teamParentSku);
    if (!metaobjectHandle) {
      console.warn(
        '[TeamPdpMetaobjectService] Missing team store handle for parent sku',
        teamParentSku
      );
      return { metaobject: null, teamStoreCollection: null };
    }

    const query = TEAM_STORE_METAOBJECT_QUERY;
    const variables = { handle: metaobjectHandle };

    try {
      const result = await this.client.fetch(
        /** @type {string} */ (query),
        variables
      );

      this._markMediaLoaded();

      if (!result.ok || !result.data) {
        console.error(
          '[TeamPdpMetaobjectService] GraphQL failed:',
          result.error
        );
        return { metaobject: null, teamStoreCollection: null };
      }

      /** @type {TeamPdpMetaobjectQueryData} */
      const data = result.data;
      const node = data.metaobject;

      if (!node) {
        console.warn(
          '[TeamPdpMetaobjectService] No metaobject found for handle',
          metaobjectHandle
        );
        return { metaobject: null, teamStoreCollection: null };
      }

      const metaobject = this._normalizeMetaobjectFields(node.fields || []);
      const teamStoreCollection = this._normalizeTeamStoreCollection(
        node.team_store_collection
      );

      metaobject.handle = metaobjectHandle;
      metaobject.isTeamStoreClosed =
        this._isTeamStoreClosed(teamStoreCollection);

      if (metaobject) {
        this._dispatchMetaobjectEvent(metaobject, teamStoreCollection);
      }

      return { metaobject, teamStoreCollection };
    } catch (error) {
      this._markMediaLoaded();
      console.error('[TeamPdpMetaobjectService] Unexpected error:', error);
      return { metaobject: null, teamStoreCollection: null };
    }
  }

  /**
   * Build metaobject
   * @private
   * @param {string} teamParentSku

   * @returns {string|null}
   */
  _buildHandle(teamParentSku) {
    return (
      window.teamStoreUtilities?.buildTeamStoreHandle?.({
        teamParentSku,
        opportunityNumber: window.teamProductData?.opportunityNumber,
      }) || null
    );
  }

  /**
   * Convert metaobject.fields array into a simple key/value map.
   *
   * @private
   * @param {MetaobjectField[]} fields
   * @returns {Record<string, any>}
   */
  _normalizeMetaobjectFields(fields) {
    return fields.reduce((acc, field) => {
      if (!field || !field.key) return acc;
      acc[field.key] = field.value;
      return acc;
    }, /** @type {Record<string, any>} */ ({}));
  }

  /**
   * Normalize team_store_collection metafields into a plain object.
   *
   * @private
   * @param {{ reference: TeamStoreCollectionReference|null }|null} teamStoreCollectionField
   * @returns {Record<string, any>|null}
   */
  _normalizeTeamStoreCollection(teamStoreCollectionField) {
    const reference = teamStoreCollectionField?.reference;
    if (!reference) return null;

    const metafieldsArray = reference.metafields || [];

    /** @type {Record<string, any>} */
    const normalized = metafieldsArray.reduce((acc, field) => {
      if (!field || !field.key || field.value == null) return acc;
      acc[field.key] = field.value;
      return acc;
    }, /** @type {Record<string, any>} */ ({}));

    normalized.id = reference.id;
    normalized.url = reference.onlineStoreUrl;

    return normalized;
  }

  /**
   * Determine if team store is closed based on start/end dates.
   *
   * @private
   * @param {Record<string, any>|null} teamStoreCollection
   * @returns {boolean}
   */
  _isTeamStoreClosed(teamStoreCollection) {
    const now = Date.now();

    const start = teamStoreCollection?.start_date
      ? new Date(teamStoreCollection.start_date).getTime()
      : null;
    const end = teamStoreCollection?.end_date
      ? new Date(teamStoreCollection.end_date).getTime()
      : null;

    if (!start || !end) return true;
    if (end && now > end) return true;
    if (start && now < start) return true;

    return false;
  }

  /**
   * Mark product media lists as loaded (remove "loading", add "loaded").
   *
   * @private
   */
  _markMediaLoaded() {
    const doc = this.win.document;
    const elements = doc.querySelectorAll('.product__media-list');
    elements.forEach((element) => {
      element.classList.remove('loading');
      element.classList.add('loaded');
    });
  }

  /**
   * Dispatch a global event once metaobject & collection are ready.
   *
   * @private
   * @param {Record<string, any>} metaobject
   * @param {Record<string, any>|null} teamStoreCollection
   */
  _dispatchMetaobjectEvent(metaobject, teamStoreCollection) {
    window.TeamStoreMetaobjects ??= {};

    if (metaobject.handle) {
      window.TeamStoreMetaobjects[metaobject.handle] = {
        metaobject,
        teamStoreCollection,
      };
    }

    const event = new CustomEvent('metaobjectDataRetrieved', {
      detail: { metaobject, teamStoreCollection },
    });
    this.win.dispatchEvent(event);
  }
}


/**
 * @typedef {object} MetaobjectEventDetail
 * @property {string} handle
 * @property {Record<string, any>} metaobject
 * @property {Record<string, any>|null} [teamStoreCollection]
 */

/**
 * Subscribe to metaobjectDataRetrieved events for a specific handle.
 *
 * Usage example
 * --------------------------------------------
 * const handle = window.teamStoreUtilities.buildTeamStoreHandle({
 *   teamParentSku: bundle_product.metafields.custom.parent_sku,
 *   opportunityNumber: window.teamProductData.opportunityNumber,
 * });
 *
 * const unsubscribe = window.TeamStoreEvents.subscribeToMetaobject(
 *   handle,
 *   (metaobject, teamStoreCollection) => {
 *     console.log('Metaobject event received:', { metaobject, teamStoreCollection });
 *   }
 * );
 *
 * // Later if needed:
 * // unsubscribe();
 *
 * @param {string} handle
 * @param {(metaobject: Record<string, any>, teamStoreCollection: Record<string, any>|null) => void} callback
 * @returns {() => void} unsubscribe function
 */
function subscribeToMetaobject(handle, callback) {
  const cachedEntry = window.TeamStoreMetaobjects?.[handle];
  if (cachedEntry?.metaobject) {
    callback(cachedEntry.metaobject, cachedEntry.teamStoreCollection || null);
    return () => {};
  }

  /** @param {CustomEvent<MetaobjectEventDetail>} event */
  const listener = (event) => {
    const detail = event.detail;

    if (!detail?.metaobject?.handle || detail.metaobject.handle !== handle) {
      return;
    }

    callback(detail.metaobject, detail.teamStoreCollection || null);
  };

  window.addEventListener('metaobjectDataRetrieved', listener);

  return () => {
    window.removeEventListener('metaobjectDataRetrieved', listener);
  };
}

function markTeamPdpMediaLoaded(doc = window.document) {
  const elements = doc.querySelectorAll('.product__media-list');
  elements.forEach((element) => {
    element.classList.remove('loading');
    element.classList.add('loaded');
  });
}

/**
 * @param {Record<string, any>|null|undefined} metaobject
 */
function applyTeamPdpLastNameVisibility(metaobject) {
  const lastNameField = window.document.getElementById('lastNameField');
  if (!lastNameField || !metaobject) return;

  const isPersonalization = metaobject.personalization;
  const isApproved = metaobject.proof_status;

  if (
    isPersonalization === 'true' &&
    isApproved === 'Approved' &&
    !metaobject.isTeamStoreClosed
  ) {
    lastNameField.style.display = 'block';
  }
}

/**
 * @param {Record<string, any>|null|undefined} metaobject
 */
function applyTeamPdpDefaultGallery(metaobject) {
  if (!metaobject) return;

  const isDefaultGallery = metaobject.include_default_images;
  const galleries = window.document.querySelectorAll('.product__media-list');
  const thumbnails = window.document.querySelectorAll('.thumbnail-slider');

  if (isDefaultGallery == 'false') {
    galleries.forEach((element) => {
      element.classList.add('hide-default');
    });
    thumbnails.forEach((element) => {
      element.classList.add('hide-default');
    });
    return;
  }

  galleries.forEach((gallery) => {
    gallery.classList.add('show-default');
    const galleryImages = gallery.querySelectorAll('.product__media-item');

    galleryImages.forEach((media) => {
      const el = /** @type {HTMLElement} */ (media);
      const dataImageIndex = Number(el.getAttribute('data-image-index'));

      if (!Number.isNaN(dataImageIndex)) {
        if (
          (dataImageIndex >= 10 && dataImageIndex <= 19) ||
          (dataImageIndex >= 30 && dataImageIndex <= 39)
        ) {
          // Keep model/detail images and preserve their ordering.
          el.style.order = String(dataImageIndex);
        } else {
          el.classList.add('hidden');
        }
      } else {
        el.classList.add('hidden');
      }
    });
  });
}

/**
 * Appends a custom team image to PDP containers.
 *
 * @param {string} selector - Selector for image containers.
 * @param {string} parentSelector - Selector for parent wrapper to unhide.
 * @param {string} blobUrl - Blob URL of the generated image.
 * @param {number} imageIndex - Index used to identify the custom image.
 */
function appendTeamPdpImage(selector, parentSelector, blobUrl, imageIndex) {
  const containers = window.document.querySelectorAll(selector);

  containers.forEach((element) => {
    const customImageContainer = element.closest(parentSelector);
    customImageContainer?.classList.remove('hidden');

    if (
      element.querySelector(
        `img[data-team-custom-image-index="${String(imageIndex)}"]`
      )
    ) {
      return;
    }

    const image = window.document.createElement('img');
    image.src = blobUrl;
    image.setAttribute('data-team-custom-image-index', String(imageIndex));
    element.appendChild(image);
  });
}

/**
 * @param {TeamProductData} teamProductData
 * @param {Record<string, any>|null|undefined} metaobject
 */
function applyTeamPdpCustomImages(teamProductData, metaobject) {
  const teamParentSku = teamProductData?.teamParentSku || '';
  const opportunityNumber = metaobject?.opportunity_id;

  if (!teamParentSku || !opportunityNumber || teamParentSku.length <= 5) return;

  const skuTeamPrefix = teamParentSku.slice(0, 5);
  const productParentSku = teamParentSku.slice(5);

  for (let i = 1; i <= 3; i += 1) {
    const imgHandle = `${skuTeamPrefix}${opportunityNumber}_${productParentSku}__P${i}.webp`;
    const imageUrl = `/cdn/shop/files/${imgHandle}`;

    fetch(imageUrl)
      .then((response) => {
        markTeamPdpMediaLoaded();
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        return response.blob();
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        appendTeamPdpImage(`.Slide-P${i}`, '.product__media-item', blobUrl, i);
        appendTeamPdpImage(
          `.Slide-P${i}--mobile`,
          '.product__media-item--custom',
          blobUrl,
          i
        );
      })
      .catch((error) => {
        console.error('Error loading image:', error);
      });
  }
}

/**
 * @param {TeamProductData} [teamProductData]
 * @param {Record<string, any>|null|undefined} [metaobject]
 */
function applyTeamPdpMetaobjectEffects(
  teamProductData = window.teamProductData || {},
  metaobject = teamProductData?.metaobject
) {
  if (!metaobject) return;

  applyTeamPdpLastNameVisibility(metaobject);
  applyTeamPdpDefaultGallery(metaobject);
  applyTeamPdpCustomImages(teamProductData, metaobject);
}

/**
 * Initialize Team PDP metaobject loading + UI side effects.
 * Uses handle-based subscription to avoid cross-product event collisions.
 * @param {TeamProductData} [teamProductData]
 * @returns {Promise<{ metaobject: Record<string, any>|null, teamStoreCollection: Record<string, any>|null }|null>}
 */
function initializeTeamPdp(teamProductData = window.teamProductData || {}) {
  if (!teamProductData?.teamParentSku) {
    return Promise.resolve(null);
  }

  window.teamProductData = teamProductData;

  if (!teamProductData.opportunityNumber) {
    teamProductData.opportunityNumber =
      window.teamStoreUtilities?.resolveOpportunityNumber?.() || null;
  }

  const handle =
    window.teamStoreUtilities?.buildTeamStoreHandle?.(teamProductData) || null;
  if (!handle) {
    return Promise.resolve(null);
  }

  const cachedEntry = window.TeamStoreMetaobjects?.[handle];
  if (cachedEntry?.metaobject) {
    teamProductData.metaobject = cachedEntry.metaobject;
    if (cachedEntry.teamStoreCollection) {
      teamProductData.teamStoreCollection = cachedEntry.teamStoreCollection;
    }

    applyTeamPdpMetaobjectEffects(teamProductData, cachedEntry.metaobject);

    return Promise.resolve({
      metaobject: cachedEntry.metaobject,
      teamStoreCollection: cachedEntry.teamStoreCollection || null,
    });
  }

  window.TeamStorePdpInit ??= {};
  const existing = window.TeamStorePdpInit[handle];
  if (existing?.promise) {
    return existing.promise;
  }

  let unsubscribe = () => {};
  unsubscribe = subscribeToMetaobject(
    handle,
    /**
     * @param {Record<string, any>} metaobject
     * @param {Record<string, any>|null} teamStoreCollection
     */
    (metaobject, teamStoreCollection) => {
      teamProductData.metaobject = metaobject;
      if (teamStoreCollection) {
        teamProductData.teamStoreCollection = teamStoreCollection;
      }
      applyTeamPdpMetaobjectEffects(teamProductData, metaobject);
      unsubscribe();
    }
  );

  if (teamProductData.metaobject?.handle === handle) {
    applyTeamPdpMetaobjectEffects(teamProductData, teamProductData.metaobject);
  }

  const service = new TeamPdpMetaobjectService();
  const promise = service.load(teamProductData.teamParentSku);

  window.TeamStorePdpInit[handle] = { unsubscribe, promise };

  return promise;
}

function bootstrapTeamPdpFromWindow() {
  const teamProductData = window.teamProductData;
  if (!teamProductData?.teamParentSku) return;

  initializeTeamPdp(teamProductData).catch((error) => {
    console.error('[TeamPdpMetaobjectService] Team PDP bootstrap failed:', error);
  });
}

window.TeamStoreEvents ??= {};
window.TeamStoreServices ??= {};

window.TeamStoreEvents.subscribeToMetaobject = subscribeToMetaobject;
window.TeamStoreServices.TeamPdpMetaobjectService = TeamPdpMetaobjectService;
window.TeamStoreServices.initializeTeamPdp = initializeTeamPdp;
window.TeamStoreServices.applyTeamPdpMetaobjectEffects =
  applyTeamPdpMetaobjectEffects;

bootstrapTeamPdpFromWindow();
